import { selectConfigurationValueFrom } from '@epicuro-next/platform/configuration';
import {
  arrayIsEmpty,
  arrayIsNotEmpty,
  makeInterval,
} from '@epicuro-next/utilities';
import { createSelector } from '@ngrx/store';
import {
  InventoryItem,
  OrderType,
  Prepack,
} from 'graphql-api/gql-api-interfaces';
import { Interval } from 'luxon';

import {
  ConfigStripStateConfigurationKeys,
  ConfigStripStateConfigurations,
  FEATURE_NAME,
} from '../../../configurations';
import { DeliveryPeriodType } from '../../models';
import {
  selectCurrentlyActiveShopDeliveryItems,
  selectCurrentShopCurrentDeliveryPeriodType,
  selectCurrentShopDeliveryPeriods,
} from '../config-strip.selectors';
import { selectPrepacks } from '../data/config-strip.data.selectors';
import { countItemsTotals } from '../shared/count-items-totals';

import { getAvailabilityRanges } from './availability-range/get-availability-range';
import { selectAvailableFutureInventories } from './future-delivery-periods.selectors';

const selectCurrentShopBulkDeliveryPeriods = createSelector(
  selectCurrentShopDeliveryPeriods,
  (shopDeliveryPeriods) => shopDeliveryPeriods[DeliveryPeriodType.Bulk] ?? {},
);

export const selectBulkDeliveryPeriods = createSelector(
  selectCurrentShopBulkDeliveryPeriods,
  (bulkDeliveryPeriods) => {
    return [...(bulkDeliveryPeriods.deliveryPeriods ?? [])].sort((a, b) => {
      const { from: aFrom, to: aTo } = a;
      const { from: bFrom, to: bTo } = b;
      return (
        new Date(aFrom).getTime() - new Date(bFrom).getTime() ||
        new Date(aTo).getTime() - new Date(bTo).getTime()
      );
    });
  },
);

export const selectHasBulkDeliveryPeriods = createSelector(
  selectBulkDeliveryPeriods,
  arrayIsNotEmpty,
);

export const selectHasNoBulkDeliveryPeriods = createSelector(
  selectBulkDeliveryPeriods,
  arrayIsEmpty,
);

export const selectSelectedBulkDeliveryPeriod = createSelector(
  selectCurrentShopBulkDeliveryPeriods,
  selectCurrentShopCurrentDeliveryPeriodType,
  (bulkDeliveryPeriods, currentSelectedDeliveryPeriodType) => {
    if (currentSelectedDeliveryPeriodType !== DeliveryPeriodType.Bulk) {
      return null;
    }

    return bulkDeliveryPeriods.selectedDeliveryPeriod;
  },
);

export const selectBulkDeliveryItemTotals = createSelector(
  selectPrepacks,
  selectCurrentlyActiveShopDeliveryItems,
  (prepacks, deliveryItems) => {
    return Object.keys(deliveryItems)
      .filter(
        (periodKey) =>
          deliveryItems[periodKey].orderType === OrderType.Future &&
          deliveryItems[periodKey].deliveryPeriodType ===
            DeliveryPeriodType.Bulk,
      )
      .reduce((acc, periodKey) => {
        const numberOfItemsInPeriod = countItemsTotals(
          deliveryItems[periodKey].items,
          prepacks as Partial<Prepack>[],
        );

        return {
          ...acc,
          [periodKey]: acc[periodKey]
            ? acc[periodKey] + numberOfItemsInPeriod
            : numberOfItemsInPeriod,
        };
      }, {} as { [period: string]: number });
  },
);

export const selectConfigStripBulkDatePickerRanges = createSelector(
  selectConfigurationValueFrom<
    ConfigStripStateConfigurationKeys.ActiveVariantDateRangeEnabled,
    ConfigStripStateConfigurations
  >(
    FEATURE_NAME,
    ConfigStripStateConfigurationKeys.ActiveVariantDateRangeEnabled,
    true,
  ),
  selectAvailableFutureInventories(false),
  selectAvailableFutureInventories(true),
  (useActiveVariant, variantInventories, globalInventories) =>
    getAvailabilityRanges(
      useActiveVariant ? variantInventories : globalInventories,
    ),
);

export const selectPredefinedBulkDeliveryPeriods = createSelector(
  selectConfigurationValueFrom<
    ConfigStripStateConfigurationKeys.BulkDeliveryPeriods,
    ConfigStripStateConfigurations
  >(FEATURE_NAME, ConfigStripStateConfigurationKeys.BulkDeliveryPeriods, ''),
  (value: string) => {
    try {
      const config: { name: string; start: string; end: string }[] =
        JSON.parse(value);

      const mappedPeriods = config.map((item) => ({
        name: item.name,
        interval: makeInterval(item.start, item.end),
      }));

      return mappedPeriods;
    } catch {
      return null;
    }
  },
);

export const selectConfigStripBulkDatePickerPredefinedRanges = createSelector(
  selectPredefinedBulkDeliveryPeriods,
  selectAvailableFutureInventories(false),
  (
    periods: { name: string; interval: Interval }[] | null,
    activeVariantInventories: InventoryItem[],
  ) => {
    if (!periods?.length) {
      return null;
    }

    if (!activeVariantInventories.length) {
      return periods;
    }

    return periods.filter((period) => {
      return activeVariantInventories.some((item: InventoryItem) =>
        period.interval.overlaps(
          makeInterval(item.availableFromDateUtc, item.availableToDateUtc),
        ),
      );
    });
  },
);

export const selectConfigStripBulkMaxRange = createSelector(
  selectConfigStripBulkDatePickerRanges,
  (intervals: Interval[]) => {
    return {
      from: intervals.slice(0, 1).pop()?.start.toJSDate(),
      to: intervals.slice(-1).pop()?.end.toJSDate(),
    };
  },
);
